import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuCalendarClock, LuLoader, LuSend, LuTriangleAlert } from 'react-icons/lu';
import { CampaignsCreditPreSendCheck, extractWhatsappTemplateCategory, formatCreditDateTime, useCreditValidityInputs } from '@/features/campaigns/v2/credits';
import { buildFlowInstancePayload } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/flowPayload';
import { FlowFormData } from '@/features/campaigns/components/CreateCampaign_v2/steps/Whatsapp/types';
import { useCreateCampaignV2Mutation, useUpdateCampaignV2Mutation } from '@/services/endpoints/campaigns';
import { useCreateFlowInstanceMutation } from '@/services/endpoints/workspace-connectors';
import { Channel } from '../../NewCommunication/ChannelStepContainer';
import { useNewCommunicationValues } from '../../NewCommunication/context/useNewCommunicationValues';
import { useNewCommunicationActions } from '../../NewCommunication/context/useNewCommunicationActions';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useComuniTabLock } from '../../ComuniTabLockContext';
import { NO_TOPIC } from '../../comuni-helper';

export type SentResult = 'now' | 'scheduled' | 'draft';
export type Counts = { total: number; withPhone: number; withEmail: number; unreachable: number };

type ChannelOutcome = {
  status: 'done' | 'error';
  message?: string;
  campaignId?: string;
  flowInstanceId?: string;
};
type SendMode = 'now' | 'schedule';

const CHANNEL_ORDER: Channel[] = ['whatsapp', 'sms', 'email'];
const PROVIDER_BY_CHANNEL: Record<Channel, 'WHATSAPP' | 'SMS' | 'EMAIL'> = {
  whatsapp: 'WHATSAPP',
  sms: 'SMS',
  email: 'EMAIL',
};

/** Best-effort template label from a send flow's raw params — shape varies per connector. */
export const channelTemplateLabel = (flowParams?: FlowFormData): string => {
  const raw = flowParams?.['template'] ?? flowParams?.['template_id'] ?? flowParams?.['templateId'];
  return raw != null ? String(raw) : '—';
};

const toDateTimeLocalValue = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * ISO schedule bounds for the confirm call: null/null outside 'schedule' mode or without a start.
 * Defensive clamp at the confirm boundary (mirrors the inputs' `min`/auto-bump intent so a stale
 * value never silently turns into an immediate send): a past start is clamped to now, and an end
 * at or before the (possibly clamped) start is bumped to start+1h.
 */
export const buildScheduleIso = (
  sendMode: SendMode,
  scheduleStart: string,
  scheduleEnd: string
): { scheduledStart: string | null; scheduledEnd: string | null } => {
  if (sendMode !== 'schedule' || !scheduleStart) return { scheduledStart: null, scheduledEnd: null };
  const now = new Date();
  const parsedStart = new Date(scheduleStart);
  const start = parsedStart < now ? now : parsedStart;
  const parsedEnd = scheduleEnd ? new Date(scheduleEnd) : null;
  const end = parsedEnd && parsedEnd > start ? parsedEnd : new Date(start.getTime() + 60 * 60 * 1000);
  return { scheduledStart: start.toISOString(), scheduledEnd: end.toISOString() };
};

type ChannelCreditGateProps = {
  channel: Channel;
  audienceId: string;
  sendMode: SendMode;
  scheduleStart?: Date;
  flowParams?: FlowFormData;
  onValidityChange: (channel: Channel, canSend: boolean) => void;
};

/** One credit pre-send check per channel — mounted separately so each gets its own hook call. */
const ChannelCreditGate: FC<ChannelCreditGateProps> = ({ channel, audienceId, sendMode, scheduleStart, flowParams, onValidityChange }) => {
  const { i18n } = useTranslation('campaign');
  const templateCategory = channel === 'whatsapp' ? extractWhatsappTemplateCategory(flowParams as Record<string, unknown> | undefined) : undefined;
  const { pricing, at } = useCreditValidityInputs({
    channel,
    audienceId,
    scheduleType: sendMode === 'schedule' ? 'schedule' : 'sendNow',
    scheduleStart,
    templateCategory,
  });
  const scheduleLabel = useMemo(
    () => (sendMode === 'schedule' && scheduleStart ? formatCreditDateTime(scheduleStart.toISOString(), i18n.language) : undefined),
    [sendMode, scheduleStart, i18n.language]
  );
  const handleValidityChange = useCallback((canSend: boolean) => onValidityChange(channel, canSend), [channel, onValidityChange]);
  return <CampaignsCreditPreSendCheck channel={channel} pricing={pricing} at={at} scheduleLabel={scheduleLabel} onValidityChange={handleValidityChange} />;
};

export const PreviewSendStep: FC = () => {
  const { t } = useTranslation('comuni');
  const { t: tCampaign } = useTranslation('campaign');
  const { id: workspaceId } = useWorkspace();

  const { channels, channelConfigs: configs, draftAudienceId: audienceId, communicationName, topic, counts, showCosts } = useNewCommunicationValues();
  const { goToPreviousStep, setSent } = useNewCommunicationActions();
  const { setTabsLocked } = useComuniTabLock();

  const onDone = (result: SentResult) => {
    setSent(result);
  };

  const orderedSelected = useMemo(() => CHANNEL_ORDER.filter((channel) => channels.includes(channel)), [channels]);
  const reachableCount = counts ? Math.max(0, counts.total - counts.unreachable) : null;
  const topicLabel = topic === NO_TOPIC ? t('topic.none') : t(`topics.${topic}`);

  const [sendMode, setSendMode] = useState<SendMode>('now');
  const [scheduleStart, setScheduleStart] = useState('');
  const [scheduleEnd, setScheduleEnd] = useState('');
  const [creditOkMap, setCreditOkMap] = useState<Partial<Record<Channel, boolean>>>({});
  const [outcomes, setOutcomes] = useState<Partial<Record<Channel, ChannelOutcome>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSendNow, setLastSendNow] = useState<boolean | null>(null);

  const [createFlowInstance] = useCreateFlowInstanceMutation();
  const [createCampaignV2] = useCreateCampaignV2Mutation();
  const [updateCampaignV2] = useUpdateCampaignV2Mutation();

  const handleSendModeChange = (mode: SendMode) => {
    setSendMode(mode);
    // Re-prefill on entering schedule mode whenever there's no kept value, or the kept value has
    // gone stale (e.g. the user flipped to "now" and back after the previously picked time passed).
    if (mode === 'schedule' && (!scheduleStart || new Date(scheduleStart) < new Date())) {
      const defaultStart = new Date();
      defaultStart.setSeconds(0, 0);
      const defaultEnd = new Date(defaultStart);
      defaultEnd.setHours(defaultEnd.getHours() + 1);
      setScheduleStart(toDateTimeLocalValue(defaultStart));
      setScheduleEnd(toDateTimeLocalValue(defaultEnd));
    }
  };

  const handleScheduleStartChange = (value: string) => {
    setScheduleStart(value);
    if (!value) return;
    const startDate = new Date(value);
    const currentEnd = scheduleEnd ? new Date(scheduleEnd) : null;
    if (!currentEnd || currentEnd <= startDate) {
      const defaultEnd = new Date(startDate);
      defaultEnd.setHours(defaultEnd.getHours() + 1);
      setScheduleEnd(toDateTimeLocalValue(defaultEnd));
    }
  };

  const handleChannelValidity = useCallback((channel: Channel, canSend: boolean) => {
    setCreditOkMap((prev) => (prev[channel] === canSend ? prev : { ...prev, [channel]: canSend }));
  }, []);

  const allConfigured = channels.length > 0 && channels.every((channel) => !!configs[channel]);
  const allCreditOk = channels.every((channel) => creditOkMap[channel] === true);
  const scheduleStartDate = sendMode === 'schedule' && scheduleStart ? new Date(scheduleStart) : undefined;
  // Once any channel has an outcome (a run happened — full or partial), the only way forward is
  // Retry: back-nav would lose `outcomes` and re-create already-'done' channels, and a fresh
  // Draft/Confirm click could mix a different sendNow into a partially-completed run.
  const hasOutcome = Object.keys(outcomes).length > 0;
  const locked = isSubmitting || hasOutcome;

  // Keep Comuni tabs disabled for the same window so switching away cannot unmount the provider
  // (draft-audience cleanup + lost outcome IDs → duplicate campaigns on return).
  useEffect(() => {
    setTabsLocked(locked);
    return () => setTabsLocked(false);
  }, [locked, setTabsLocked]);

  const confirm = async (sendNow: boolean) => {
    if (isSubmitting) return; // re-entrancy guard: no concurrent runs from a double click
    setLastSendNow(sendNow);
    setIsSubmitting(true);
    const { scheduledStart, scheduledEnd } = sendNow ? buildScheduleIso(sendMode, scheduleStart, scheduleEnd) : { scheduledStart: null, scheduledEnd: null };
    const status = sendNow ? 'ACTIVE' : 'DRAFT';
    for (const channel of orderedSelected) {
      const prior = outcomes[channel];
      if (prior?.status === 'done') continue; // retry skips channels already created
      const config = configs[channel];
      if (!config) continue;
      // Resume mid-triplet on Retry: keep IDs from a prior partial failure so we do not
      // re-createFlow / re-createCampaign when only updateCampaign failed (or createCampaign after flow).
      let flowInstanceId = prior?.flowInstanceId;
      let campaignId = prior?.campaignId;
      try {
        if (!flowInstanceId) {
          const flowInstance = await createFlowInstance(
            buildFlowInstancePayload({
              workspaceId,
              connectorFlowId: config.connectorFlowId,
              connectorInstanceId: config.connectorInstanceId,
              audienceId,
              flowParams: config.flowParams,
              flowMappings: config.flowMappings,
            })
          ).unwrap();
          flowInstanceId = flowInstance.id;
        }
        if (!campaignId) {
          const draftCampaign = await createCampaignV2({
            workspaceId,
            name: communicationName,
            status,
            providerType: PROVIDER_BY_CHANNEL[channel],
            audienceId,
            connectorInstanceId: config.connectorInstanceId,
            scheduledStart,
            scheduledEnd,
          }).unwrap();
          campaignId = draftCampaign.id;
        }
        await updateCampaignV2({ workspaceId, campaignId, status, connectorFlowInstanceId: flowInstanceId }).unwrap();
        setOutcomes((prev) => ({ ...prev, [channel]: { status: 'done', campaignId, flowInstanceId } }));
      } catch (error) {
        const message = (error as { data?: { message?: string } })?.data?.message ?? String(tCampaign('flowInstanceCreationFailed'));
        setOutcomes((prev) => ({
          ...prev,
          [channel]: { status: 'error', message, campaignId, flowInstanceId },
        }));
        setIsSubmitting(false);
        return; // stop at the first failed channel; the UI shows the error + a Retry button
      }
    }
    setIsSubmitting(false);
    onDone(sendNow ? (sendMode === 'schedule' ? 'scheduled' : 'now') : 'draft');
  };

  const failedChannel = orderedSelected.find((channel) => outcomes[channel]?.status === 'error');

  return (
    <div>
      <section className='mt-6 rounded-2xl border px-10 py-9'>
        <h3 className='text-[26px] font-semibold tracking-tight text-primary-900'>{t('review.title')}</h3>
        <p className='mt-1.5 text-[15px] text-tertiary-600'>{t('review.subtitle')}</p>

        <dl className='mt-6 grid gap-5 sm:grid-cols-2'>
          <div>
            <dt className='text-[11.5px] font-medium uppercase tracking-wide text-quaternary-400'>{t('review.name')}</dt>
            <dd className='mt-1 text-[15px] font-semibold text-primary-900'>{communicationName}</dd>
          </div>
          <div>
            <dt className='text-[11.5px] font-medium uppercase tracking-wide text-quaternary-400'>{t('review.recipients')}</dt>
            <dd className='mt-1 text-[15px] font-semibold text-primary-900'>~{reachableCount ?? '—'}</dd>
          </div>
          <div>
            <dt className='text-[11.5px] font-medium uppercase tracking-wide text-quaternary-400'>{t('review.topic')}</dt>
            <dd className='mt-1 text-[15px] font-semibold text-primary-900'>{topicLabel}</dd>
          </div>
          <div>
            <dt className='text-[11.5px] font-medium uppercase tracking-wide text-quaternary-400'>{t('review.channels')}</dt>
            <dd className='mt-1 flex flex-wrap gap-2'>
              {orderedSelected.map((channel) => (
                <span key={channel} className='rounded-full border bg-secondary-background px-3 py-1 text-[13px] font-medium text-primary-900'>
                  {t(`channel.${channel}`)}
                </span>
              ))}
            </dd>
          </div>
        </dl>

        <div className='mt-6 space-y-2 border-t pt-5'>
          <div className='text-[11.5px] font-medium uppercase tracking-wide text-quaternary-400'>{t('review.templates')}</div>
          {orderedSelected.map((channel) => {
            const reach = channel === 'email' ? counts?.withEmail : counts?.withPhone;
            return (
              <div key={channel} className='flex items-center justify-between text-[14px] text-primary-900'>
                <span className='font-medium'>{t(`channel.${channel}`)}</span>
                <span className='text-tertiary-600'>
                  {channelTemplateLabel(configs[channel]?.flowParams)}
                  {showCosts && reach != null ? ` · ${t('channel.estimatedCost', { count: reach })}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      <section className='mt-6 rounded-2xl border px-10 py-9'>
        <h3 className='text-[19px] font-semibold tracking-tight text-primary-900'>{t('review.modeTitle')}</h3>
        <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <button
            type='button'
            disabled={locked}
            onClick={() => handleSendModeChange('now')}
            className={`flex items-start gap-3 rounded-[14px] border p-5 text-left disabled:cursor-default disabled:opacity-60 ${
              sendMode === 'now' ? 'border-2 border-brand-600 bg-brand-50/40 p-[19px]' : 'bg-white-100 hover:border-brand-200'
            }`}
          >
            <LuSend className='mt-0.5 size-5 flex-none text-brand-700' />
            <span>
              <span className='block text-[14.5px] font-semibold text-primary-900'>{t('review.sendNow')}</span>
              <span className='mt-1 block text-[13px] text-tertiary-600'>{t('review.sendNowHint')}</span>
            </span>
          </button>
          <button
            type='button'
            disabled={locked}
            onClick={() => handleSendModeChange('schedule')}
            className={`flex items-start gap-3 rounded-[14px] border p-5 text-left disabled:cursor-default disabled:opacity-60 ${
              sendMode === 'schedule' ? 'border-2 border-brand-600 bg-brand-50/40 p-[19px]' : 'bg-white-100 hover:border-brand-200'
            }`}
          >
            <LuCalendarClock className='mt-0.5 size-5 flex-none text-brand-700' />
            <span>
              <span className='block text-[14.5px] font-semibold text-primary-900'>{t('review.schedule')}</span>
              <span className='mt-1 block text-[13px] text-tertiary-600'>{t('review.scheduleHint')}</span>
            </span>
          </button>
        </div>

        {sendMode === 'schedule' && (
          <div className='mt-5 grid gap-4 rounded-[14px] border bg-primary-background p-5 sm:grid-cols-2'>
            <label className='block'>
              <span className='text-[12.5px] font-medium text-primary-900'>{t('review.scheduleStart')}</span>
              <input
                type='datetime-local'
                value={scheduleStart}
                min={toDateTimeLocalValue(new Date())}
                step={900}
                disabled={locked}
                onChange={(ev) => handleScheduleStartChange(ev.target.value)}
                className='mt-1.5 w-full rounded-[9px] border bg-white-100 px-3 py-2.5 text-[13.5px] text-primary-900 outline-none focus:border-brand-600 disabled:opacity-60'
              />
            </label>
            <label className='block'>
              <span className='text-[12.5px] font-medium text-primary-900'>{t('review.scheduleEnd')}</span>
              <input
                type='datetime-local'
                value={scheduleEnd}
                min={scheduleStart || toDateTimeLocalValue(new Date())}
                step={900}
                disabled={locked}
                onChange={(ev) => setScheduleEnd(ev.target.value)}
                className='mt-1.5 w-full rounded-[9px] border bg-white-100 px-3 py-2.5 text-[13.5px] text-primary-900 outline-none focus:border-brand-600 disabled:opacity-60'
              />
            </label>
          </div>
        )}

        <div className='mt-5 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13.5px] text-amber-800'>
          <LuTriangleAlert className='mt-0.5 size-4 flex-none' />
          <span>{t('review.irreversibleWarning')}</span>
        </div>
      </section>

      {orderedSelected.length > 0 && (
        <section className='mt-6 space-y-4'>
          {orderedSelected.map((channel) => (
            <ChannelCreditGate
              key={channel}
              channel={channel}
              audienceId={audienceId}
              sendMode={sendMode}
              scheduleStart={scheduleStartDate}
              flowParams={configs[channel]?.flowParams}
              onValidityChange={handleChannelValidity}
            />
          ))}
        </section>
      )}

      {failedChannel && (
        <div className='mt-6 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-5 py-4'>
          <p className='text-[13.5px] text-red-700'>
            {t('review.channelError', { channel: t(`channel.${failedChannel}`), message: outcomes[failedChannel]?.message })}
          </p>
          <button
            type='button'
            disabled={isSubmitting}
            onClick={() => void confirm(lastSendNow ?? true)}
            className='flex-none rounded-[9px] border border-red-300 bg-white-100 px-4 py-2 text-[13px] font-medium text-red-700 hover:bg-red-100 disabled:cursor-default disabled:opacity-60'
          >
            {t('review.retry')}
          </button>
        </div>
      )}

      <div className='mt-8 flex items-center justify-between'>
        <button
          type='button'
          disabled={locked}
          onClick={goToPreviousStep}
          className='rounded-[10px] border bg-white-100 px-7 py-3 text-[15px] font-medium text-primary-900 hover:bg-secondary-background disabled:cursor-default disabled:opacity-60'
        >
          {t('composer.back')}
        </button>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            disabled={!allConfigured || locked}
            onClick={() => void confirm(false)}
            className={`rounded-[10px] border px-7 py-3 text-[15px] font-medium ${
              !allConfigured || locked
                ? 'cursor-default border-quaternary-200 text-quaternary-400'
                : 'border-brand-200 bg-white-100 text-brand-700 hover:bg-brand-50'
            }`}
          >
            {t('review.saveAsDraft')}
          </button>
          <button
            type='button'
            disabled={!allConfigured || locked || !allCreditOk || (sendMode === 'schedule' && !scheduleStart)}
            onClick={() => void confirm(true)}
            className={`flex items-center gap-2 rounded-[10px] px-9 py-3 text-[15px] font-medium ${
              !allConfigured || locked || !allCreditOk || (sendMode === 'schedule' && !scheduleStart)
                ? 'cursor-default bg-quaternary-100 text-quaternary-400'
                : 'bg-brand-700 text-white-100 hover:bg-brand-800'
            }`}
          >
            {isSubmitting && <LuLoader className='size-4 animate-spin' />}
            {t('review.confirmAndSend')}
          </button>
        </div>
      </div>
    </div>
  );
};
